import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Switch,
  FormControlLabel,
  Card,
  CardContent
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  Cloud as CloudIcon,
  Settings as SettingsIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { getProcedureCodesPaginated } from '../../services/procedureCodesService';
import { 
  getFacilityCPTPrices, 
  saveFacilityCPTPrices,
  copyPricesFromFacility 
} from '../../services/facilityPricingService';
import { 
  getEDIConfiguration, 
  getBillingConfiguration, 
  saveEDIConfiguration, 
  saveBillingConfiguration 
} from '../../services/configurationService';
import ClearinghouseService from '../../services/clearinghouseService';
import { getBillingConfig, updateBillingConfig } from '../../services/monthEndCloseService';

const FacilityBillingTab = ({ facilityId, availableFacilities = [] }) => {
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Clearinghouse state
  const [clearinghouseConfig, setClearinghouseConfig] = useState({
    provider: '',
    configured: false,
    credentials: { apiKey: '', submitterId: '' },
    settings: { testMode: true, autoRetry: true, maxRetries: 3 }
  });

  // EDI state
  const [ediConfig, setEdiConfig] = useState({
    submitter: { name: '', npi: '', etin: '', contactName: '', phone: '', email: '' },
    receiver: { name: '', npi: '', etin: '' },
    payer: { name: '', payerId: '', address: '' },
    payee: { name: '', npi: '', taxId: '' }
  });

  // Billing automation state
  const [billingConfig, setBillingConfig] = useState({
    automaticGeneration: false,
    eligibilityCheck: false,
    batchProcessing: false,
    retryAttempts: 3,
    retryDelay: 5000,
    autoGenerateClaims: false,
    claimSubmissionDelay: 24,
    requirePriorAuthorization: false
  });

  // Billing Settings state (includes Month End Close)
  const [billingSettings, setBillingSettings] = useState({
    // Billing Configuration
    defaultDueDate: 30,
    defaultPaymentTerms: 'net_30',
    defaultTaxRate: 0.1,
    enableLateFees: false,
    lateFeePercentage: 0.02,
    gracePeriod: 15,
    minimumPayment: 25,
    // Payment Methods
    paymentMethods: {
      cash: { enabled: true, label: 'Cash' },
      credit_card: { enabled: true, label: 'Credit Card' },
      debit_card: { enabled: true, label: 'Debit Card' },
      bank_transfer: { enabled: true, label: 'Bank Transfer' },
      check: { enabled: true, label: 'Check' },
      insurance: { enabled: true, label: 'Insurance' }
    },
    // Month End Close
    monthEndCloseEnabled: true,
    accountingMethod: 'accrual',
    autoHardCloseGracePeriodDays: 5
  });
  const [billingSettingsLoading, setBillingSettingsLoading] = useState(false);
  const [billingSettingsSaving, setBillingSettingsSaving] = useState(false);

  // CPT pricing state
  const [procedureCodes, setProcedureCodes] = useState([]);
  const [, setFacilityPrices] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [copyFromFacility, setCopyFromFacility] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [cptLoading, setCptLoading] = useState(false);
  const [cptPage, setCptPage] = useState(0);
  const [cptRowsPerPage, setCptRowsPerPage] = useState(20);

  // Helper function to add timeout to promises
  const withTimeout = (promise, ms = 5000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))
    ]);
  };

  // Track which tabs have been loaded
  const [clearinghouseLoaded, setClearinghouseLoaded] = useState(false);
  const [ediLoaded, setEdiLoaded] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [cptLoaded, setCptLoaded] = useState(false);

  // Reset loaded states when facilityId changes to force data reload
  useEffect(() => {
    setClearinghouseLoaded(false);
    setEdiLoaded(false);
    setBillingLoaded(false);
    setCptLoaded(false);
    // Also reset data states
    setFacilityPrices({});
    setEditedPrices({});
    setProcedureCodes([]);
    setHasChanges(false);
  }, [facilityId]);

  // Load clearinghouse config (only when tab is first viewed)
  const loadClearinghouse = useCallback(async () => {
    // Valid clearinghouse providers
    const VALID_PROVIDERS = ['OFFICE_ALLY', 'AVAILITY', 'CHANGE_HEALTHCARE', 'TRIZETTO'];
    if (clearinghouseLoaded || !facilityId) return;
    setLoading(true);
    try {
      const clearinghouseService = new ClearinghouseService();
      const chConfig = await withTimeout(clearinghouseService.getConfigurationStatus(), 5000);
      if (chConfig) {
        // Normalize provider value - only use if it's a valid option
        const provider = VALID_PROVIDERS.includes(chConfig.provider) ? chConfig.provider : '';
        setClearinghouseConfig({
          provider,
          configured: chConfig.configured || false,
          credentials: chConfig.credentials || { apiKey: '', submitterId: '' },
          settings: chConfig.settings || { testMode: true, autoRetry: true, maxRetries: 3 }
        });
      }
      setClearinghouseLoaded(true);
    } catch (err) {
      console.log('Clearinghouse config not available:', err.message);
      setClearinghouseLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [facilityId, clearinghouseLoaded]);

  // Load EDI config (only when tab is first viewed)
  const loadEdi = useCallback(async () => {
    if (ediLoaded || !facilityId) return;
    setLoading(true);
    try {
      const edi = await withTimeout(getEDIConfiguration(), 5000);
      if (edi) {
        setEdiConfig({
          submitter: edi.submitter || { name: '', npi: '', etin: '', contactName: '', phone: '', email: '' },
          receiver: edi.receiver || { name: '', npi: '', etin: '' },
          payer: edi.payer || { name: '', payerId: '', address: '' },
          payee: edi.payee || { name: '', npi: '', taxId: '' }
        });
      }
      setEdiLoaded(true);
    } catch (err) {
      console.log('EDI config not available:', err.message);
      setEdiLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [facilityId, ediLoaded]);

  // Load billing config (only when tab is first viewed)
  const loadBilling = useCallback(async () => {
    if (billingLoaded || !facilityId) return;
    setLoading(true);
    try {
      const billing = await withTimeout(getBillingConfiguration(), 5000);
      if (billing) {
        setBillingConfig(prev => ({ ...prev, ...billing }));
      }
      setBillingLoaded(true);
    } catch (err) {
      console.log('Billing config not available:', err.message);
      setBillingLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [facilityId, billingLoaded]);

  // Load all CPT codes with pagination support
  const loadAllCptCodes = async () => {
    let allCodes = [];
    let hasMore = true;
    let cursor = null;
    const pageSize = 1000;

    while (hasMore) {
      const result = await getProcedureCodesPaginated({
        procedureCodeType: 'CPT4',
        activeOnly: true,
        pageSize
      }, pageSize, cursor);

      const codes = result?.procedureCodes || [];
      allCodes = [...allCodes, ...codes];

      // Check if there are more pages
      const pagination = result?.pagination;
      if (pagination?.hasMore && codes.length > 0) {
        cursor = codes[codes.length - 1]?.id;
      } else {
        hasMore = false;
      }

      // Safety limit to prevent infinite loops
      if (allCodes.length > 10000) {
        console.warn('CPT codes limit reached, stopping pagination');
        hasMore = false;
      }
    }

    return allCodes;
  };

  // Load CPT codes and prices (only when CPT tab is first viewed)
  const loadCptData = useCallback(async () => {
    if (cptLoaded || !facilityId) return;
    setCptLoading(true);
    try {
      // Load CPT codes and prices in parallel
      const [cptResult, pricesResult] = await Promise.allSettled([
        withTimeout(loadAllCptCodes(), 30000),
        withTimeout(getFacilityCPTPrices(facilityId), 5000)
      ]);

      // Handle CPT codes
      if (cptResult.status === 'fulfilled') {
        const codes = cptResult.value || [];
        setProcedureCodes(Array.isArray(codes) ? codes : []);
        console.log(`✅ Loaded ${codes.length} CPT4 codes (all pages)`);
      } else {
        console.log('CPT codes not available:', cptResult.reason?.message);
        setProcedureCodes([]);
      }

      // Handle prices
      if (pricesResult.status === 'fulfilled') {
        const priceMap = {};
        (pricesResult.value || []).forEach(p => {
          priceMap[p.procedureCodeId] = p.price;
        });
        setFacilityPrices(priceMap);
        setEditedPrices(priceMap);
      } else {
        console.log('Facility prices not available:', pricesResult.reason?.message);
        setFacilityPrices({});
        setEditedPrices({});
      }

      setCptLoaded(true);
    } catch (err) {
      console.log('Error loading CPT data:', err.message);
      setCptLoaded(true);
    } finally {
      setCptLoading(false);
    }
  }, [facilityId, cptLoaded]);

  // Load data based on active tab (lazy loading)
  useEffect(() => {
    if (!facilityId) return;
    
    switch (activeSubTab) {
      case 0:
        loadClearinghouse();
        break;
      case 1:
        loadEdi();
        break;
      case 2:
        loadBilling();
        break;
      case 3:
        loadCptData();
        break;
      default:
        break;
    }
  }, [activeSubTab, facilityId, loadClearinghouse, loadEdi, loadBilling, loadCptData]);

  // Pre-load clearinghouse on mount (first tab)
  useEffect(() => {
    if (facilityId && activeSubTab === 0) {
      loadClearinghouse();
    }
  }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save clearinghouse config
  const saveClearinghouseConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      const clearinghouseService = new ClearinghouseService();
      await clearinghouseService.saveConfiguration(clearinghouseConfig);
      setSuccess('Clearinghouse configuration saved successfully');
    } catch (err) {
      setError('Failed to save clearinghouse configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save EDI config
  const saveEdiConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveEDIConfiguration(ediConfig);
      setSuccess('EDI configuration saved successfully');
    } catch (err) {
      setError('Failed to save EDI configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save billing automation config
  const saveBillingAutomation = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveBillingConfiguration(billingConfig);
      setSuccess('Billing automation settings saved successfully');
    } catch (err) {
      setError('Failed to save billing automation settings');
    } finally {
      setSaving(false);
    }
  };

  // Save CPT prices
  const saveCptPrices = async () => {
    setSaving(true);
    setError(null);
    try {
      const pricesToSave = procedureCodes
        .filter(code => {
          const price = editedPrices[code.id];
          return price && price > 0;
        })
        .map(code => ({
          procedureCodeId: code.id,
          procedureCode: code.procedure_code || code.code,
          procedureName: code.name || code.description,
          price: editedPrices[code.id]
        }));

      await saveFacilityCPTPrices(facilityId, pricesToSave);
      setFacilityPrices({ ...editedPrices });
      setHasChanges(false);
      setSuccess(`Successfully saved ${pricesToSave.length} prices`);
    } catch (err) {
      setError('Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  // Handle price change
  const handlePriceChange = (codeId, value) => {
    const numValue = value === '' ? '' : parseFloat(value) || 0;
    setEditedPrices(prev => ({ ...prev, [codeId]: numValue }));
    setHasChanges(true);
  };

  // Handle copy from facility
  const handleCopyFrom = async (sourceFacilityId) => {
    if (!sourceFacilityId) return;
    setCptLoading(true);
    try {
      const result = await copyPricesFromFacility(sourceFacilityId, facilityId);
      // Reload facility prices after copy
      const prices = await getFacilityCPTPrices(facilityId);
      const priceMap = {};
      (prices || []).forEach(p => {
        priceMap[p.procedureCodeId] = p.price;
      });
      setFacilityPrices(priceMap);
      setEditedPrices(priceMap);
      setHasChanges(false);
      setSuccess(result.message || 'Prices copied successfully');
    } catch (err) {
      setError('Failed to copy prices');
    } finally {
      setCptLoading(false);
      setCopyFromFacility('');
    }
  };

  // Filter codes
  const filteredCodes = procedureCodes.filter(code => {
    const codeStr = (code.procedure_code || code.code || '').toLowerCase();
    const nameStr = (code.name || code.description || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    const matchesSearch = codeStr.includes(search) || nameStr.includes(search);
    if (filterTab === 1) {
      const price = editedPrices[code.id];
      return matchesSearch && price && price > 0;
    }
    return matchesSearch;
  });

  // Paginated codes for display
  const paginatedCodes = filteredCodes.slice(
    cptPage * cptRowsPerPage,
    cptPage * cptRowsPerPage + cptRowsPerPage
  );

  const pricedCount = procedureCodes.filter(code => {
    const price = editedPrices[code.id];
    return price && price > 0;
  }).length;

  // Pagination handlers
  const handleCptPageChange = (event, newPage) => {
    setCptPage(newPage);
  };

  const handleCptRowsPerPageChange = (event) => {
    setCptRowsPerPage(parseInt(event.target.value, 10));
    setCptPage(0);
  };

  // Reset page when search or filter changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCptPage(0);
  };

  const handleFilterTabChange = (e, v) => {
    setFilterTab(v);
    setCptPage(0);
  };

  // Render Clearinghouse Tab
  const renderClearinghouseTab = () => (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Clearinghouse Configuration</Typography>
          <Chip
            icon={clearinghouseConfig.configured ? <CheckCircleIcon /> : <ErrorIcon />}
            label={clearinghouseConfig.configured ? 'Configured' : 'Not Configured'}
            color={clearinghouseConfig.configured ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={saveClearinghouseConfig} disabled={saving}>
          Save
        </Button>
      </Box>

      {/* Provider & Credentials Card */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            Provider & Credentials
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Clearinghouse Provider</InputLabel>
                <Select
                  value={clearinghouseConfig.provider}
                  onChange={(e) => setClearinghouseConfig(prev => ({ ...prev, provider: e.target.value }))}
                  label="Clearinghouse Provider"
                >
                  <MenuItem value="">Select Provider</MenuItem>
                  <MenuItem value="OFFICE_ALLY">Office Ally</MenuItem>
                  <MenuItem value="AVAILITY">Availity</MenuItem>
                  <MenuItem value="CHANGE_HEALTHCARE">Change Healthcare</MenuItem>
                  <MenuItem value="TRIZETTO">Trizetto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="API Key"
                type="password"
                value={clearinghouseConfig.credentials?.apiKey || ''}
                onChange={(e) => setClearinghouseConfig(prev => ({
                  ...prev,
                  credentials: { ...prev.credentials, apiKey: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Submitter ID"
                value={clearinghouseConfig.credentials?.submitterId || ''}
                onChange={(e) => setClearinghouseConfig(prev => ({
                  ...prev,
                  credentials: { ...prev.credentials, submitterId: e.target.value }
                }))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Settings Card */}
      <Card variant="outlined">
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            Submission Settings
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={clearinghouseConfig.settings?.testMode || false}
                  onChange={(e) => setClearinghouseConfig(prev => ({
                    ...prev,
                    settings: { ...prev.settings, testMode: e.target.checked }
                  }))}
                />
              }
              label={<Typography variant="body2">Test Mode</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={clearinghouseConfig.settings?.autoRetry || false}
                  onChange={(e) => setClearinghouseConfig(prev => ({
                    ...prev,
                    settings: { ...prev.settings, autoRetry: e.target.checked }
                  }))}
                />
              }
              label={<Typography variant="body2">Auto Retry</Typography>}
            />
            <TextField
              size="small"
              type="number"
              label="Max Retries"
              value={clearinghouseConfig.settings?.maxRetries || 3}
              onChange={(e) => setClearinghouseConfig(prev => ({
                ...prev,
                settings: { ...prev.settings, maxRetries: parseInt(e.target.value) || 3 }
              }))}
              sx={{ width: 100 }}
              InputProps={{ inputProps: { min: 1, max: 10 } }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  // Render EDI Tab
  const renderEdiTab = () => (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>EDI Settings</Typography>
        <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={saveEdiConfig} disabled={saving}>
          Save
        </Button>
      </Box>

      {/* Submitter Section */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            Submitter Information
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Organization Name"
                value={ediConfig.submitter?.name || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  submitter: { ...prev.submitter, name: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="NPI"
                value={ediConfig.submitter?.npi || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  submitter: { ...prev.submitter, npi: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="ETIN"
                value={ediConfig.submitter?.etin || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  submitter: { ...prev.submitter, etin: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Contact Name"
                value={ediConfig.submitter?.contactName || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  submitter: { ...prev.submitter, contactName: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                value={ediConfig.submitter?.phone || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  submitter: { ...prev.submitter, phone: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={ediConfig.submitter?.email || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  submitter: { ...prev.submitter, email: e.target.value }
                }))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payer Section */}
      <Card variant="outlined">
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            Payer Information
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Payer Name"
                value={ediConfig.payer?.name || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  payer: { ...prev.payer, name: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Payer ID"
                value={ediConfig.payer?.payerId || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  payer: { ...prev.payer, payerId: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Address"
                value={ediConfig.payer?.address || ''}
                onChange={(e) => setEdiConfig(prev => ({
                  ...prev,
                  payer: { ...prev.payer, address: e.target.value }
                }))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  // Render Billing Automation Tab
  const renderBillingAutomationTab = () => (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Billing Automation</Typography>
        <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={saveBillingAutomation} disabled={saving}>
          Save
        </Button>
      </Box>

      {/* Automation Toggles */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            Automation Features
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <Switch
                  size="small"
                  checked={billingConfig.autoGenerateClaims || false}
                  onChange={(e) => setBillingConfig(prev => ({ ...prev, autoGenerateClaims: e.target.checked }))}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Auto-Generate Claims</Typography>
                  <Typography variant="caption" color="text.secondary">Generate claims from completed encounters</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <Switch
                  size="small"
                  checked={billingConfig.eligibilityCheck || false}
                  onChange={(e) => setBillingConfig(prev => ({ ...prev, eligibilityCheck: e.target.checked }))}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Eligibility Verification</Typography>
                  <Typography variant="caption" color="text.secondary">Check eligibility before submission</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <Switch
                  size="small"
                  checked={billingConfig.batchProcessing || false}
                  onChange={(e) => setBillingConfig(prev => ({ ...prev, batchProcessing: e.target.checked }))}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Batch Processing</Typography>
                  <Typography variant="caption" color="text.secondary">Process claims in batches</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <Switch
                  size="small"
                  checked={billingConfig.requirePriorAuthorization || false}
                  onChange={(e) => setBillingConfig(prev => ({ ...prev, requirePriorAuthorization: e.target.checked }))}
                />
                <Box sx={{ ml: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Prior Authorization</Typography>
                  <Typography variant="caption" color="text.secondary">Flag encounters requiring auth</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Timing Settings */}
      <Card variant="outlined">
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            Timing & Retry Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Submission Delay (hrs)"
                value={billingConfig.claimSubmissionDelay || 24}
                onChange={(e) => setBillingConfig(prev => ({ ...prev, claimSubmissionDelay: parseInt(e.target.value) || 24 }))}
                InputProps={{ inputProps: { min: 0, max: 168 } }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Retry Attempts"
                value={billingConfig.retryAttempts || 3}
                onChange={(e) => setBillingConfig(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) || 3 }))}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Retry Delay (ms)"
                value={billingConfig.retryDelay || 5000}
                onChange={(e) => setBillingConfig(prev => ({ ...prev, retryDelay: parseInt(e.target.value) || 5000 }))}
                InputProps={{ inputProps: { min: 1000, max: 60000, step: 1000 } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  // Load Billing Settings
  const loadBillingSettings = useCallback(async () => {
    if (!facilityId) return;
    setBillingSettingsLoading(true);
    try {
      const config = await getBillingConfig(facilityId);
      if (config) {
        setBillingSettings(prev => ({
          ...prev,
          // Billing Configuration
          defaultDueDate: config.defaultDueDate ?? 30,
          defaultPaymentTerms: config.defaultPaymentTerms || 'net_30',
          defaultTaxRate: config.defaultTaxRate ?? 0.1,
          enableLateFees: config.enableLateFees ?? false,
          lateFeePercentage: config.lateFeePercentage ?? 0.02,
          gracePeriod: config.gracePeriod ?? 15,
          minimumPayment: config.minimumPayment ?? 25,
          // Payment Methods
          paymentMethods: config.paymentMethods || prev.paymentMethods,
          // Month End Close
          monthEndCloseEnabled: config.monthEndCloseEnabled !== false,
          accountingMethod: config.accountingMethod || 'accrual',
          autoHardCloseGracePeriodDays: config.autoHardCloseGracePeriodDays ?? 5
        }));
      }
    } catch (err) {
      console.error('Error loading billing settings:', err);
    } finally {
      setBillingSettingsLoading(false);
    }
  }, [facilityId]);

  // Save Billing Settings
  const saveBillingSettings = async () => {
    if (!facilityId) return;
    setBillingSettingsSaving(true);
    try {
      await updateBillingConfig(facilityId, billingSettings);
      setSuccess('Billing settings saved successfully');
    } catch (err) {
      console.error('Error saving billing settings:', err);
      setError('Failed to save billing settings');
    } finally {
      setBillingSettingsSaving(false);
    }
  };

  // Toggle payment method
  const handlePaymentMethodToggle = (method) => {
    setBillingSettings(prev => ({
      ...prev,
      paymentMethods: {
        ...prev.paymentMethods,
        [method]: {
          ...prev.paymentMethods[method],
          enabled: !prev.paymentMethods[method].enabled
        }
      }
    }));
  };

  // Load Billing Settings when tab is selected
  useEffect(() => {
    if (facilityId && activeSubTab === 4) {
      loadBillingSettings();
    }
  }, [facilityId, activeSubTab, loadBillingSettings]);

  // Render Billing Settings Tab
  const renderBillingSettingsTab = () => (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Billing Settings</Typography>
        <Button 
          variant="contained" 
          size="small" 
          startIcon={<SaveIcon />} 
          onClick={saveBillingSettings} 
          disabled={billingSettingsSaving}
        >
          {billingSettingsSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {billingSettingsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {/* Invoice & Payment Settings */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Invoice & Payment Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Default Due Date (days)"
                    value={billingSettings.defaultDueDate}
                    onChange={(e) => setBillingSettings(prev => ({ ...prev, defaultDueDate: parseInt(e.target.value) || 30 }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Default Payment Terms</InputLabel>
                    <Select
                      value={billingSettings.defaultPaymentTerms}
                      label="Default Payment Terms"
                      onChange={(e) => setBillingSettings(prev => ({ ...prev, defaultPaymentTerms: e.target.value }))}
                    >
                      <MenuItem value="due_on_receipt">Due on Receipt</MenuItem>
                      <MenuItem value="net_15">Net 15</MenuItem>
                      <MenuItem value="net_30">Net 30</MenuItem>
                      <MenuItem value="net_45">Net 45</MenuItem>
                      <MenuItem value="net_60">Net 60</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Default Tax Rate (%)"
                    value={(billingSettings.defaultTaxRate * 100).toFixed(0)}
                    onChange={(e) => setBillingSettings(prev => ({ ...prev, defaultTaxRate: (parseFloat(e.target.value) || 0) / 100 }))}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Minimum Payment Amount ($)"
                    value={billingSettings.minimumPayment}
                    onChange={(e) => setBillingSettings(prev => ({ ...prev, minimumPayment: parseFloat(e.target.value) || 0 }))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Late Fees */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Late Fees
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={billingSettings.enableLateFees}
                        onChange={(e) => setBillingSettings(prev => ({ ...prev, enableLateFees: e.target.checked }))}
                      />
                    }
                    label="Enable Late Fees"
                  />
                </Grid>
                {billingSettings.enableLateFees && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Late Fee Percentage (%)"
                        value={(billingSettings.lateFeePercentage * 100).toFixed(0)}
                        onChange={(e) => setBillingSettings(prev => ({ ...prev, lateFeePercentage: (parseFloat(e.target.value) || 0) / 100 }))}
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Grace Period (days)"
                        value={billingSettings.gracePeriod}
                        onChange={(e) => setBillingSettings(prev => ({ ...prev, gracePeriod: parseInt(e.target.value) || 0 }))}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Accepted Payment Methods
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(billingSettings.paymentMethods).map(([method, config]) => (
                  <Grid item xs={6} sm={4} key={method}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={config.enabled}
                          onChange={() => handlePaymentMethodToggle(method)}
                        />
                      }
                      label={config.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Month End Close */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Month End Close
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={billingSettings.monthEndCloseEnabled}
                        onChange={(e) => setBillingSettings(prev => ({ ...prev, monthEndCloseEnabled: e.target.checked }))}
                      />
                    }
                    label="Enable Month End Close"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5 }}>
                    Track billing periods and lock them to prevent modifications after close.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Accounting Method</InputLabel>
                    <Select
                      value={billingSettings.accountingMethod}
                      label="Accounting Method"
                      onChange={(e) => setBillingSettings(prev => ({ ...prev, accountingMethod: e.target.value }))}
                      disabled={!billingSettings.monthEndCloseEnabled}
                    >
                      <MenuItem value="accrual">Accrual Basis</MenuItem>
                      <MenuItem value="cash">Cash Basis</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Accrual: Revenue when earned. Cash: Revenue when received.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Auto Hard Close Grace Period (Days)"
                    value={billingSettings.autoHardCloseGracePeriodDays}
                    onChange={(e) => setBillingSettings(prev => ({ 
                      ...prev, 
                      autoHardCloseGracePeriodDays: parseInt(e.target.value) || 0 
                    }))}
                    disabled={!billingSettings.monthEndCloseEnabled}
                    inputProps={{ min: 0, max: 30 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Days after soft close before automatic hard close.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );

  // Render CPT Pricing Tab
  const renderCptPricingTab = () => {
    if (cptLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading CPT codes...</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>CPT Code Pricing</Typography>
            <Typography variant="caption" color="text.secondary">
              Set prices for procedure codes • Only codes with prices will be saved
            </Typography>
          </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Copy From</InputLabel>
            <Select
              value={copyFromFacility}
              onChange={(e) => {
                setCopyFromFacility(e.target.value);
                if (e.target.value) handleCopyFrom(e.target.value);
              }}
              label="Copy From"
              disabled={availableFacilities.length === 0}
            >
              <MenuItem value="">
                <em>{availableFacilities.length === 0 ? 'No facilities' : 'Select...'}</em>
              </MenuItem>
              {availableFacilities.map(f => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={saveCptPrices}
            disabled={saving || !hasChanges}
          >
            Save
          </Button>
        </Box>
      </Box>

      {/* Search & Filter Bar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search codes..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }}
        />
        <Tabs 
          value={filterTab} 
          onChange={handleFilterTabChange} 
          sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, px: 1.5, fontSize: '0.8rem' } }}
        >
          <Tab label={`All (${procedureCodes.length})`} />
          <Tab label={`Priced (${pricedCount})`} />
        </Tabs>
        {hasChanges && <Chip label="Unsaved" color="warning" size="small" variant="outlined" />}
      </Box>

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50', py: 1 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50', py: 1 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50', py: 1, width: 80 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50', py: 1, width: 110 }}>Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No codes match your search' : 'No procedure codes available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCodes.map(code => {
                  const price = editedPrices[code.id] || '';
                  const hasPrice = price && price > 0;
                  return (
                    <TableRow key={code.id} hover sx={{ bgcolor: hasPrice ? 'success.50' : 'inherit' }}>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.8rem' }}>
                          {code.procedure_code || code.code}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }} noWrap>
                          {code.name || code.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Chip label={code.procedure_code_type || 'N/A'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={price}
                          onChange={(e) => handlePriceChange(code.id, e.target.value)}
                          placeholder="0.00"
                          InputProps={{
                            startAdornment: <InputAdornment position="start" sx={{ mr: 0 }}>$</InputAdornment>,
                            inputProps: { min: 0, step: 0.01 },
                            sx: { fontSize: '0.8rem' }
                          }}
                          sx={{ 
                            width: 95, 
                            '& .MuiOutlinedInput-root': { 
                              bgcolor: hasPrice ? 'success.50' : 'inherit',
                              '& input': { py: 0.5, px: 1 }
                            } 
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredCodes.length}
          page={cptPage}
          onPageChange={handleCptPageChange}
          rowsPerPage={cptRowsPerPage}
          onRowsPerPageChange={handleCptRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {filteredCodes.length} of {procedureCodes.length} codes • {pricedCount} with prices
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ m: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs
        value={activeSubTab}
        onChange={(e, v) => setActiveSubTab(v)}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
      >
        <Tab icon={<CloudIcon />} iconPosition="start" label="Clearinghouse" />
        <Tab icon={<CodeIcon />} iconPosition="start" label="EDI Settings" />
        <Tab icon={<SettingsIcon />} iconPosition="start" label="Automation" />
        <Tab icon={<SettingsIcon />} iconPosition="start" label="CPT Pricing" />
        <Tab icon={<SettingsIcon />} iconPosition="start" label="Billing Settings" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeSubTab === 0 && renderClearinghouseTab()}
          {activeSubTab === 1 && renderEdiTab()}
          {activeSubTab === 2 && renderBillingAutomationTab()}
          {activeSubTab === 3 && renderCptPricingTab()}
          {activeSubTab === 4 && renderBillingSettingsTab()}
        </>
      )}
    </Box>
  );
};

export default FacilityBillingTab;

